import React, { useState } from "react"
import { Dialog, DialogContent, Box, Typography, Button, Rating, TextField } from "@mui/material"
import { motion, AnimatePresence } from "framer-motion"
// import confetti from "canvas-confetti"
import { useDemo } from "../../../context/DemoContext"
import { toast } from "react-toastify"

const interestOptions = [
  { value: "high", label: "Very Interested", icon: "🔥" },
  { value: "medium", label: "Somewhat", icon: "🙂" },
  { value: "low", label: "Not Really", icon: "🤔" },
]

const followUpOptions = [
  { value: "call", label: "Give me a call", icon: "📞" },
  { value: "email", label: "Send me an email", icon: "✉️" },
  { value: "demo", label: "Schedule a demo", icon: "📅" },
]

export default function PremiumFeedbackModal({ open, onClose, onSubmit, handleCTAClick }) {
  const [activeStep, setActiveStep] = useState(0)
  const [submitted, setSubmitted] = useState(false)
  const { submitDemoFeedback } = useDemo()

  const [formValues, setFormValues] = useState({
    rating: 0,
    interestLevel: "",
    phoneNumber: "",
    followUp: "",
    demoTime: null,
    feedback: "",
  })

  const validateStep = () => {
    const step = steps[activeStep]

    if (step === "Rate Experience") {
      if (!formValues.rating || formValues.rating < 1) {
        return "Please provide a rating"
      }
    }

    if (step === "Interest Level") {
      if (!formValues.interestLevel) {
        return "Please select your interest level"
      }
    }

    if (step === "Phone Number") {
      if (!formValues.phoneNumber) {
        return "Phone number is required"
      }

      const isValidPhone = /^[0-9+\-\s()]{7,15}$/.test(formValues.phoneNumber)
      if (!isValidPhone) {
        return "Enter a valid phone number"
      }
    }

    if (step === "Follow-up") {
      if (!formValues.followUp) {
        return "Please select a follow-up option"
      }
    }

    return null
  }

  // Dynamically determine steps based on follow-up choice
  const steps = ["Rate Experience", "Interest Level", "Feedback", "Follow-up"]
  // if (formValues.followUp === "demo") steps.push("Schedule Demo")
  // steps.push("Feedback") // always final step

  const handleChange = (field, value) => setFormValues((prev) => ({ ...prev, [field]: value }))

  const handleNext = () => {
    const error = validateStep()

    if (error) {
      toast.error(error)
      return
    }

    if (activeStep < steps.length - 1) {
      setActiveStep((s) => s + 1)
    } else {
      handleSubmit()
    }
  }

  const handleBack = () => setActiveStep((s) => s - 1)

  const handleSubmit = async () => {
    const success = await submitDemoFeedback({
      rating: formValues.rating,
      interestLevel: formValues.interestLevel,
      selectedMethod: formValues.followUp,
      demoTime: formValues.demoTime,
      notes: formValues.feedback,
    })

    if (!success) return

    setSubmitted(true)

    if (formValues.followUp === "demo") {
      handleCTAClick()
    }
    // confetti({
    //   particleCount: 120,
    //   spread: 80,
    //   origin: { y: 0.6 },
    // })
    // onSubmit?.(formValues)
  }

  // Render current step content
  const renderStepContent = () => {
    const step = steps[activeStep]

    switch (step) {
      case "Rate Experience":
        return (
          <Box textAlign="center">
            <Typography sx={{ color: "rgba(255,255,255,0.8)", mb: 2 }}>How would you rate DashRecon so far?</Typography>
            <Rating size="large" value={formValues.rating} onChange={(e, v) => handleChange("rating", v)} sx={{ fontSize: 42, color: "var(--va-tertiary-bg)" }} />
          </Box>
        )
      case "Interest Level":
        return (
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>How excited are you about exploring DashRecon further?</Typography>
            {interestOptions.map((option) => {
              const selected = formValues.interestLevel === option.value
              return (
                <motion.div key={option.value} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <Box
                    onClick={() => handleChange("interestLevel", option.value)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      cursor: "pointer",
                      padding: "16px",
                      borderRadius: "14px",
                      transition: "all .25s",
                      background: selected ? "linear-gradient(135deg,var(--va-primary-color),var(--va-primary-color-1))" : "rgba(255,255,255,0.06)",
                      border: selected ? "2px solid white" : "1px solid rgba(255,255,255,0.2)",
                    }}
                  >
                    <Typography fontSize={22}>{option.icon}</Typography>
                    <Typography sx={{ color: "#fff", fontWeight: 600 }}>{option.label}</Typography>
                  </Box>
                </motion.div>
              )
            })}
          </Box>
        )
      case "Phone Number":
        return (
          <TextField
            label="Phone Number"
            placeholder="e.g. +1234567890"
            value={formValues.phoneNumber}
            onChange={(e) => handleChange("phoneNumber", e.target.value)}
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                color: "#fff",
                borderRadius: "12px",
                background: "rgba(255,255,255,0.08)",
              },
              "& .MuiFormLabel-root": {
                color: "var(--va-primary-bg)",
              },

              "& .Mui-focused fieldset": {
                borderColor: "var(--va-primary-bg) !important",
              },
            }}
          />
        )
      case "Follow-up":
        return (
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>We’d love to connect! How would you like us to follow up?</Typography>
            {followUpOptions.map((option) => {
              const selected = formValues.followUp === option.value
              return (
                <motion.div key={option.value} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <Box
                    onClick={() => handleChange("followUp", option.value)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      cursor: "pointer",
                      padding: "16px",
                      borderRadius: "14px",
                      transition: "all .25s",
                      background: selected ? "linear-gradient(135deg,var(--va-primary-color),var(--va-primary-color-1))" : "rgba(255,255,255,0.06)",
                      border: selected ? "2px solid white" : "1px solid rgba(255,255,255,0.2)",
                    }}
                  >
                    <Typography fontSize={22}>{option.icon}</Typography>
                    <Typography sx={{ color: "#fff", fontWeight: 600 }}>{option.label}</Typography>
                  </Box>
                </motion.div>
              )
            })}
          </Box>
        )
      case "Schedule Demo":
        return (
          <Box>
            <Button
              onClick={handleCTAClick}
              sx={{
                px: 6,
                py: 1.6,
                fontWeight: 700,
                fontSize: 14,
                borderRadius: "999px",
                textTransform: "none",
                letterSpacing: "0.3px",
                background: "linear-gradient(135deg, var(--va-primary-color), var(--va-primary-color-1))",
                color: "#fff",
                boxShadow: "0 10px 26px rgba(26, 43, 194, 0.28)",
                "&:hover": { transform: "translateY(-2px)", boxShadow: "0 16px 36px rgba(26, 43, 194, 0.38)" },
                "&:active": { transform: "translateY(0)", boxShadow: "0 8px 18px rgba(26, 43, 194, 0.25)" },
              }}
            >
              Want a deeper dive? Choose a time that works best for you
            </Button>

            {/* <Typography sx={{ color: "rgba(255,255,255,0.8)", mb: 2 }}>u</Typography>
            <DateTimePicker
              onChange={(value) => handleChange("demoTime", value)}
              value={formValues.demoTime}
              minDate={new Date()}
              calendarIcon={null}
              clearIcon={null}
              format="y-MM-dd h:mm a"
              className="text-white"
            /> */}
          </Box>
        )
      case "Feedback":
        return (
          <TextField
            multiline
            minRows={3}
            placeholder="Share your thoughts..."
            value={formValues.feedback}
            onChange={(e) => handleChange("feedback", e.target.value)}
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                color: "#fff",
                borderRadius: "12px",
                background: "rgba(255,255,255,0.08)",
              },
            }}
          />
        )
      default:
        return null
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperComponent={motion.div}
      PaperProps={{
        initial: { opacity: 0, y: -40, scale: 0.95 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: 40 },
        transition: { duration: 0.4 },
        sx: {
          borderRadius: "20px",
          margin: { xs: 2, sm: 4 }, // spacing from edges on mobile
          width: "calc(100%)",
          maxWidth: { xs: "100%", sm: 480 },
          background: "rgba(255,255,255,0.08)",
          backdropFilter: "blur(16px)",
          border: "1px solid rgba(255,255,255,0.15)",
          boxShadow: "0 20px 80px rgba(0,0,0,0.45)",
          position: "relative",
        },
      }}
    >
      {/* Glow Border */}
      <Box
        sx={{
          position: "absolute",
          inset: "-2px",
          borderRadius: "20px",
          background: "linear-gradient(135deg,var(--va-primary-color),var(--va-primary-color-1))",
          filter: "blur(18px)",
          opacity: 0.6,
          zIndex: -1,
          width: "calc(100%)",
          height: "100%",
        }}
      />

      <DialogContent
        // sx={{ p: 4, width: 420, maxWidth: "calc(100vw - 70px)" }}
        sx={{
          p: { xs: 2, sm: 4 },
        }}
      >
        <AnimatePresence mode="wait">
          {submitted ? (
            <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
              <Box textAlign="center">
                <Typography sx={{ fontSize: 40, mb: 1 }}>🎉</Typography>
                <Typography sx={{ fontSize: { xs: 16, sm: 22 }, fontWeight: 700, color: "#fff" }}>Thank You!</Typography>
                <Typography sx={{ mt: 1, color: "rgba(255,255,255,0.7)" }}>Your feedback helps improve DashRecon.</Typography>
                <Button
                  onClick={onClose}
                  sx={{
                    mt: 3,
                    background: "linear-gradient(135deg,var(--va-primary-color),var(--va-primary-color-1))",
                    color: "#fff",
                    borderRadius: "999px",
                    px: 4,
                  }}
                >
                  Close
                </Button>
              </Box>
            </motion.div>
          ) : (
            <motion.div key={activeStep} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}>
              {/* STEP HEADER */}
              <Box textAlign="center" mb={3}>
                <Typography sx={{ fontWeight: 700, fontSize: 20, color: "#fff" }}>{steps[activeStep]}</Typography>

                {/* Step Dots */}
                <Box mt={2} display="flex" justifyContent="center" gap={1}>
                  {steps.map((_, i) => (
                    <motion.div
                      key={i}
                      animate={{
                        width: activeStep === i ? 28 : 8,
                        background: activeStep === i ? "var(--va-primary-color)" : "rgba(255,255,255,0.25)",
                      }}
                      style={{ height: 8, borderRadius: 10 }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Step Content */}
              {renderStepContent()}

              {/* ACTIONS */}
              <Box mt={4} display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2} justifyContent="space-between">
                <Button
                  fullWidth
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  variant="outlined"
                  sx={{
                    color: "#fff !important",
                    borderColor: "rgba(255,255,255,0.5) !important",
                    borderRadius: "999px !important",
                    px: 3,
                    py: 0.2,
                    "&:hover": { borderColor: "#fff", background: "rgba(255,255,255,0.1)" },
                    flex: { sm: 1, md: 0 },
                    minWidth: "100px",
                  }}
                >
                  Back
                </Button>

                <motion.div whileHover={{ scale: 1.05 }}>
                  <Button
                    onClick={handleNext}
                    fullWidth
                    sx={{
                      px: 4,
                      borderRadius: "999px",
                      background: "linear-gradient(135deg,var(--va-primary-color),var(--va-primary-color-1))",
                      color: "#fff",
                      fontWeight: 600,
                      flex: { sm: 1 },
                    }}
                  >
                    {activeStep === steps.length - 1 ? "Submit" : "Next"}
                  </Button>
                </motion.div>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  )
}
